//@ts-check
import React, {
    useState,
    useCallback,
    useEffect,
    useMemo,
    useContext,
    useRef,
} from "react"
import { css, keyframes } from "@emotion/react"
import { addHours, formatDistance, formatDistanceToNow, getUnixTime } from "date-fns"
import useRawPromise from "../../hooks/useRawPromise"
import FieldSectionSelector from "../_control/FieldSectionSelector"
import Orientation from "../test/Orientation"
import Pitch from "../../context/Pitch"
import FormLoader from "./FormLoader"
import useBridge from "../../hooks/useBridge"
import EventDateTimePicker from "../_control/EventDateTimePicker"
import { FormattedMessage, useIntl } from "react-intl"
import { zonesToSectionState, zonesToDrillState } from "../../utils/eventUtils"
import { assignKeyColors } from "shared-ui"
import DrillSectionSelector from "../_control/DrillSectionSelector"
import EventFormSubmit from "./EventFormSubmit"
import NumberScroller from "../_control/NumberScroller"
import FancyDropdown from "../_control/FancyDropdown"
import TextField from "../_control/TextField"
import WysiwygEditor from "../wysiwyg/WysiwygEditor"
import Cta from "../_control/Cta"
import RefreshButton from "../_control/RefreshButton"
import LocaleContext from "../../context/Locale"
import useUnit from "../../hooks/useUnit"
import DatePickerCta from "../_control/DatePickerCta"
import { ReactSVG } from "react-svg"
import { colors } from "shared-ui"
import tempIcon from "../../assets/icons/weather/temperature.svg"
import rainIcon from "../../assets/icons/weather/rain.svg"
import windIcon from "../../assets/icons/weather/wind.svg"
import waterIcon from "../../assets/icons/weather/water.svg"
import noIcon from "../../assets/icons/no.svg"
import RequiredMark from "./RequiredMark"
import dp from 'dompurify'
import { useNavigate } from "react-router"

const columns = 7
const rows = 5

export default function MatchForm({ eventType, event, onPostRequest }) {
    //utils
    const { post, put, get } = useRawPromise()
    const { formatMessage } = useIntl()
    const { datefnsLocale } = useContext(LocaleContext)
    const { formatUnit } = useUnit()

    //field state
    const [selectedDateTime, setSelectedDateTime] = useState(/** @type {Date | undefined} */(undefined))
    const prevSelectedDateTimeRef = useRef(/** @type {Date | undefined} */(undefined))

    useEffect(() => {
        if (!selectedDateTime) return
        if (prevSelectedDateTimeRef.current && prevSelectedDateTimeRef.current.valueOf() !== selectedDateTime.valueOf()) {
            setWeatherData({})
        }
        prevSelectedDateTimeRef.current = selectedDateTime
    }, [selectedDateTime])

    const [matchType, setMatchType] = useState(/** @type {string | undefined} */(undefined))
    const [grassHeight, setGrassHeight] = useState(22)
    const [opponent, setOpponent] = useState("")
    const [selectedWateringTypes, setSelectedWateringTypes] = useState(/** @type {string[]} */([]))
    const [drillSections, setDrillSections] = useState(/** @type {(string | null)[][]} */([]))
    const [fetchingPreviousMap, setFetchingPreviousMap] = useState(false)

    const handleFetchPreviousMap = async () => {
        if (!pitch || !selectedDateTime) return
        setFetchingPreviousMap(true)
        try {
            const { data } = await get(
                `/api/current/pitches/${pitch.id}/events/latest`,
                {
                    query: {
                        "timestamp": getUnixTime(selectedDateTime),
                        "event-type": "MatchEvent",
                    }
                }
            )
            if (data?.zoneResults) setDrillSections(zonesToDrillState(data.zoneResults))
        } catch { }
        finally { setFetchingPreviousMap(false) }
    }
    const [wornSections, setWornSections] = useState(/** @type {boolean[][]} */([]))
    const [comment, setComment] = useState("")

    //other
    const { data: teamOptions, loading: waitingForTeams } = useBridge(`/api/current/frontend-bridge/match-team-types`)
    const { data: wateringTypes, loading: waitingForWateringTypes } = useBridge(`/api/current/frontend-bridge/watering-types`)
    const { data: drillTypes, loading: waitingForDrillTypes } = useBridge(`/api/current/frontend-bridge/pitch-protection-drill-types`)
    const { data: pitch } = useContext(Pitch)

    const [latestUsageDateTime, setLatestUsageDateTime] = useState(/** @type {Date | undefined} */(undefined))

    useEffect(() => {
        // fetch the date of 'last usage' (is the latest training or match event on the pitch)
        if (!selectedDateTime || !pitch || event) return

        const fetchLatestUsage = async () => {
            try {
                const latestUsageEvent = await get(
                    `/api/current/pitches/${pitch.id}/latest-usage`,
                    { query: { timestamp: getUnixTime(selectedDateTime) } },
                )
                const time = latestUsageEvent.data?.timestamp
                if (time) setLatestUsageDateTime(new Date(time * 1000))
            } catch { }
        }

        fetchLatestUsage()
    }, [selectedDateTime, pitch, event])

    const sport = pitch?.club?.sport
    const venue = pitch?.venue?.id
    const sportWateringTypes = sport && wateringTypes?.[sport] ? Object.entries(wateringTypes[sport]) : []
    const sportDrillTypes = sport && drillTypes?.[sport] ? Object.entries(drillTypes[sport]) : []
    const sportTeamTypes = sport && teamOptions?.[sport] ? teamOptions[sport] : {} 

    const drillTypeColors = useMemo(() => {
        return assignKeyColors(sportDrillTypes.map(([codename]) => codename))
    }, [sportDrillTypes])

    const toggleWateringType = useCallback((codename) => {
        setSelectedWateringTypes(prev => {
            const toggleType = prev.includes(codename) ? 'off' : 'on'

            if (toggleType === 'off') {
                return prev.filter(p => p !== codename)
            } else {
                if (prev.includes('none') || codename === 'none') {
                    return [codename]
                }
                return [...prev, codename]
            }
        })
    }, [])

    const [weatherError, setWeatherError] = useState("")
    const [weatherData, setWeatherData] = useState(
        /** @type {{ averageTemperature?: number, totalRainfall?: number, averageWindSpeed?: number, retrievalTimestamp?: number  }} */({}),
    )
    const [weatherBusy, setWeatherBusy] = useState(false)

    const handleFetchWeather = async () => {
        setWeatherBusy(true)
        setWeatherError("")
        const LOOKAHEAD_HOURS = 2
        if (!venue) return setWeatherError("Venue: null")

        if (!selectedDateTime) {
            setWeatherBusy(false)
            return setWeatherError(formatMessage({ id: "selectATimeFirst" }))
        }

        const weatherResponse = await get(`/api/current/venues/${venue}/weather/timelines`)
        const data = weatherResponse.data?.data?.timelines?.[0]?.intervals?.map((hour) => ({
            date: new Date(Date.parse(hour.startTime)),
            ...hour.values,
        })) ?? []

        const filtered = data.filter((d) => d.date >= selectedDateTime && d.date < addHours(selectedDateTime, LOOKAHEAD_HOURS))

        if (!filtered.length) {
            setWeatherBusy(false)
            return setWeatherError(formatMessage({ id: "couldNotGetWeatherForTime" }))
        }

        // Jira RS-1030: t – t+2h averages
        const weatherConditions = /** @type {{ averageTemperature: number | undefined, totalRainfall: number | undefined, averageWindSpeed: number | undefined, retrievalTimestamp: number | undefined  }} */ ({
            averageTemperature: undefined,
            totalRainfall: undefined,
            averageWindSpeed: undefined,
            retrievalTimestamp: undefined
        })

        if (filtered.length) {
            weatherConditions.averageTemperature = Math.round(10 * filtered.map((h) => h.temperatureAvg).reduce((a, b) => a + b) / filtered.length) / 10
            weatherConditions.totalRainfall = Math.round(100 * filtered.map((h) => h.precipitationIntensityAvg).reduce((a, b) => a + b)) / 100
            weatherConditions.averageWindSpeed = Math.round(10 * filtered.map((h) => h.windSpeedAvg).reduce((a, b) => a + b) / filtered.length) / 10
            weatherConditions.retrievalTimestamp = getUnixTime(new Date())
        }

        setWeatherBusy(false)
        setWeatherData(weatherConditions)
    }

    const [formError, setFormError] = useState("")
    const [isLoading, setIsLoading] = useState(!!event)

    useEffect(() => {
        if (event) {
            async function getEventAndHydrateFormData(eventId) {
                setIsLoading(true)
                const { data } = await get(`/api/current/match-events/${eventId}`)
                setSelectedDateTime(new Date(data.timestamp * 1000))
                setMatchType(data.team)
                setGrassHeight(data.grassHeight ?? 22)
                setOpponent(data.opponent ?? '')
                setComment(data.pitchComments ?? '')
                if (data.lastUsage) setLatestUsageDateTime(new Date(data.lastUsage * 1000))
                setSelectedWateringTypes(data.wateringType ?? [])
                setWornSections(zonesToSectionState(data.zoneResults))
                setDrillSections(zonesToDrillState(data.zoneResults))
                if (data.weatherConditions?.averageTemperature !== null) {
                    setWeatherData(data.weatherConditions)
                } else {
                    setWeatherData({})
                }
                setIsLoading(false)
            }
            getEventAndHydrateFormData(event.id)
        }
    }, [event])

    const validateAndSubmit = useCallback(async () => {
        setFormError('')

        if (!matchType)
            return setFormError(formatMessage({ id: "pleaseSelectMatch" }))
        if (selectedDateTime === undefined)
            return setFormError(formatMessage({ id: "noDateTime" }))
        if (grassHeight === null || grassHeight === undefined)
            return setFormError(formatMessage({ id: "grassHeightIsNotSet" }))
        if (!selectedWateringTypes.length)
            return setFormError(formatMessage({ id: "noWateringTypeIsSelected" }))
        if (!(typeof wateringTypes === 'object'))
            return setFormError(formatMessage({ id: "formNotReady" }))
        if (!sport)
            return setFormError(formatMessage({ id: "sportContextNeededButNotFound" }))

        setIsLoading(true)

        const payload = {
            team: matchType,
            opponent: dp.sanitize(opponent),
            grassHeight: Math.round(grassHeight),
            lastUsage: !!latestUsageDateTime ? getUnixTime(latestUsageDateTime) : null,
            wateringType: selectedWateringTypes,
            weatherConditions: weatherData ?? {},
            pitchComments: dp.sanitize(comment),
            timestamp: getUnixTime(selectedDateTime),
        }

        if (event) {
            const { data: putResponse, error } = await put(`${event["@id"]}`, { body: payload })

            if (error) {
                setIsLoading(false)
                onPostRequest(false, error)
            } else if (putResponse) {
                const { data: matchData } = await get(`/api/current/match-events/${putResponse.id}`)
                await Promise.all(matchData.zoneResults.map((zone) =>
                    put(`${zone["@id"]}`, {
                        body: {
                            active: wornSections?.[zone.posY - 1]?.[zone.posX - 1] ? 1 : null,
                            pitchProtectionDrillType: drillSections?.[zone.posY - 1]?.[zone.posX - 1] ?? null,
                        }
                    })
                ))
                onPostRequest(true, matchData)
                return putResponse.id
            }
        } else {
            const newPayload = {
                ...payload,
                name: `Match event for pitch ${pitch["id"]}`,
                pitch: pitch["@id"],
            }

            const { data: postResponse, error } = await post(`/api/current/${eventType.path}`, { body: newPayload })

            if (error) {
                setIsLoading(false)
                onPostRequest(false, error)
            } else if (postResponse) {
                const { data: matchData } = await get(`/api/current/match-events/${postResponse.id}`)
                await Promise.all(matchData.zoneResults.map((zone) =>
                    put(`${zone["@id"]}`, {
                        body: {
                            active: wornSections?.[zone.posY - 1]?.[zone.posX - 1] ? 1 : null,
                            pitchProtectionDrillType: drillSections?.[zone.posY - 1]?.[zone.posX - 1] ?? null,
                        },
                    })
                ))
                onPostRequest(true, matchData)
                return postResponse.id
            }
        }
    }, [pitch, matchType, grassHeight, opponent, wornSections, drillSections, selectedDateTime, latestUsageDateTime, wateringTypes, selectedWateringTypes, comment, weatherData, sport])

    const navigate = useNavigate()

    const submitAndRedirectToReportGenerator = async () => {
        const eventId = await validateAndSubmit()
        if (eventId) {
            navigate(`/analytics?matchEvent=${eventId}`)
        }
    }

    const hasRequiredFields = useMemo(() => {
        return !!(matchType && grassHeight !== null && grassHeight !== undefined && selectedWateringTypes.length)
    }, [matchType, grassHeight, selectedWateringTypes])

    const isBusy = useMemo(() => {
        return waitingForTeams || waitingForWateringTypes || waitingForDrillTypes || isLoading
    }, [waitingForTeams, waitingForWateringTypes, waitingForDrillTypes, isLoading])

    const teamOptionsArray = sportTeamTypes
        ? Object.entries(sportTeamTypes).map(([key, value]) => ({ label: value, value: key }))
        : []

    const allowWeatherFetch = useMemo(() => {
        if (!selectedDateTime) return false
        if (selectedDateTime <= new Date()) return false
        // only allow fetching forecasts up to 7 days ahead
        if (selectedDateTime.valueOf() - new Date().valueOf() > 604800000) return false
        return true
    }, [weatherData, selectedDateTime])

    const hasWeatherData = Number.isFinite(weatherData.averageTemperature)

    return (
        <>
            {isBusy ? <FormLoader /> : !sport ? formatMessage({ id: 'pitchNeedsClubAndSport' }) :
                <>
                    <div css={style.grid}>

                        <div>
                            <EventDateTimePicker
                                unixDate={selectedDateTime}
                                setUnixDate={setSelectedDateTime}
                                disabled={false}
                            />
                        </div>

                        <div css={style.fieldColumn}>
                            {formatMessage({ id: 'opponent' })}
                            <TextField
                                value={opponent}
                                onChange={(e) => setOpponent(e.target.value)}
                            />
                        </div>

                        <div css={style.fieldColumn}>
                            <div>
                                {formatMessage({ id: 'type' })} <RequiredMark />
                            </div>
                            <FancyDropdown
                                options={teamOptionsArray}
                                value={teamOptionsArray.find((x) => x.value === matchType)}
                                onChange={(option) => setMatchType(option.value)}
                            />
                        </div>

                        <div css={style.fieldColumn}>
                            {formatMessage({ id: 'lastUsage' })}
                            <DatePickerCta
                                css={style.datePickerCta}
                                date={latestUsageDateTime}
                                label={""}
                                onDateChange={(d) => setLatestUsageDateTime(d)}
                            />
                        </div>

                        <NumberScroller
                            label={formatMessage({ id: 'grassHeight' })}
                            minVal={0}
                            maxVal={40}
                            unit="mm"
                            state={grassHeight}
                            setState={setGrassHeight}
                        />

                        <div css={style.fieldColumn}>
                            <div css={[style.labelRow, css`text-transform: capitalize;`]}>
                                {formatMessage({ id: 'weather' }).toLocaleLowerCase()}
                                {hasWeatherData && allowWeatherFetch &&
                                    <RefreshButton
                                        onClick={handleFetchWeather}
                                        disabled={!selectedDateTime}
                                        title={formatMessage({ id: 'refreshWeather' })}
                                    />
                                }
                            </div>

                            {!hasWeatherData && allowWeatherFetch &&
                                <Cta
                                    css={style.weatherCta}
                                    onClick={handleFetchWeather}
                                    disabled={!selectedDateTime}
                                >
                                    {formatMessage({ id: 'getWeatherForecast' })}
                                </Cta>
                            }

                            {weatherError}

                            <div css={style.weatherCards(hasWeatherData)}>
                                <WeatherMiniCard
                                    icon={tempIcon}
                                    label={formatMessage({ id: 'temperature' })}
                                    value={hasWeatherData ? formatUnit(weatherData.averageTemperature, "°C", { useSecondaryUnit: false, showMetricUnit: true }) : null}
                                    isShimmering={weatherBusy}
                                />
                                <WeatherMiniCard
                                    icon={rainIcon}
                                    label={formatMessage({ id: 'precipitation' })}
                                    value={hasWeatherData ? formatUnit(weatherData.totalRainfall, "mm", { useSecondaryUnit: false, showMetricUnit: true }) : null}
                                    isShimmering={weatherBusy}
                                />
                                <WeatherMiniCard
                                    icon={windIcon}
                                    label={formatMessage({ id: 'wind' })}
                                    value={hasWeatherData ? formatUnit(weatherData.averageWindSpeed, "m/s", { useSecondaryUnit: false, showMetricUnit: true }) : null}
                                    isShimmering={weatherBusy}
                                />
                            </div>

                            <div css={style.forecastLabel}>
                                {weatherData.retrievalTimestamp && selectedDateTime &&
                                    formatMessage(
                                        { id: 'forecastedXTimeBeforeEvent' },
                                        {
                                            timeDistance:
                                                formatDistance(
                                                    selectedDateTime,
                                                    new Date(weatherData.retrievalTimestamp * 1000),
                                                    { locale: datefnsLocale }
                                                )
                                        }
                                    )
                                }
                            </div>
                        </div>

                        {sportWateringTypes.length > 0 && (
                            <div css={style.fullWidthCell}>
                                <h3 css={style.sectionHeading}>
                                    {formatMessage({ id: 'watering' })}
                                    <RequiredMark />
                                </h3>
                                <div css={style.cardRow}>
                                    {sportWateringTypes.map(([codename, label]) => (
                                        <WateringTypeCard
                                            key={codename}
                                            value={codename}
                                            icon={waterIcon}
                                            label={label}
                                            selected={selectedWateringTypes.includes(codename)}
                                            onClick={() => toggleWateringType(codename)}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}

                        {sportDrillTypes.length > 0 && (
                            <div css={style.fullWidthCell}>
                                <h3 css={style.sectionHeading}>
                                    {formatMessage({ id: 'pitchProtectionMap' })}
                                </h3>
                                <DrillSectionSelector
                                    backgroundType={pitch?.pitchBackground || pitch?.club?.sport}
                                    rows={rows}
                                    columns={columns}
                                    drillTypes={sportDrillTypes}
                                    drillTypeColors={drillTypeColors}
                                    sections={drillSections}
                                    onChange={setDrillSections}
                                    onLoadPrevious={selectedDateTime ? handleFetchPreviousMap : undefined}
                                    loadingPrevious={fetchingPreviousMap}
                                />
                            </div>
                        )}

                        <div css={style.bottomSection}>
                            <div css={style.wornAreasWrapper}>
                                <h3 css={style.wornAreasHeading}>
                                    <FormattedMessage id="wornAreas" />
                                </h3>
                                <div css={style.wornAreasOverflow}>
                                    <FieldSectionSelector
                                        backgroundType={pitch?.pitchBackground || pitch?.club?.sport}
                                        rows={rows}
                                        columns={columns}
                                        sections={wornSections}
                                        onChange={setWornSections}
                                    />
                                    <Orientation css={style.compass} heading={pitch.orientation} />
                                </div>
                            </div>

                            <div css={style.noteWrapper}>
                                {formatMessage({ id: 'notes' })}
                                <WysiwygEditor
                                    css={style.wysiwygEditor}
                                    value={comment}
                                    onBlur={(value) => setComment(value)}
                                    height={"308px"}
                                />
                            </div>
                        </div>

                    </div>
                </>
            }

            <EventFormSubmit
                onSubmit={validateAndSubmit}
                loading={isBusy}
                disabled={!hasRequiredFields}
                errorMsg={formError}
            >
                <Cta
                    onClick={submitAndRedirectToReportGenerator}
                    disabled={!hasRequiredFields}
                >
                    {formatMessage({ id: 'saveAndCreateReport' })}
                </Cta>
            </EventFormSubmit>
        </>
    )
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function WateringTypeCard({ icon, label, value, selected, onClick }) {
    const isNone = value === 'none'
    return (
        <button type="button" onClick={onClick} css={wateringCard.button(selected)}>
            <ReactSVG
                src={isNone ? noIcon : icon}
                css={wateringCard.icon(selected)}
            />
            {label}
        </button>
    )
}

const wateringCard = {
    button: (selected) => css`
        background: ${colors.eventDark};
        border: 2px solid ${selected ? colors.main1 : colors.eventLight};
        border-radius: 5px;
        padding: 8px 16px;
        display: flex;
        align-items: center;
        gap: 0.5em;
        cursor: pointer;
        user-select: none;
        -webkit-user-select: none;
        color: ${selected ? colors.main1 : colors.soft};
        outline: none;
        &:hover {
            border-color: ${colors.main1};
            color: ${colors.main1};
        }
    `,
    icon: (selected) => css`
        width: 1.5em;
        height: 1.5em;
        flex-shrink: 0;
        opacity: ${selected ? 1 : 0.2};
        svg { width: 100%; height: 100%; }
    `,
}

const shimmerKeyframe = keyframes`
    0%   { background-position: 200% 0; }
    100% { background-position: -200% 0; }
`

const shimmerOverlay = css`
    &::after {
        content: "";
        position: absolute;
        inset: 0;
        background: linear-gradient(90deg, transparent 25%, rgba(255, 255, 255, 0.07) 50%, transparent 75%);
        background-size: 200% 100%;
        animation: ${shimmerKeyframe} 1.5s linear infinite;
    }
`

function WeatherMiniCard({ icon, label, value, isShimmering }) {
    return (
        <div css={[weatherCard.wrapper, isShimmering && shimmerOverlay]}>
            <ReactSVG css={weatherCard.icon} src={icon} />
            <div css={weatherCard.label}>{label}</div>
            <div css={weatherCard.value}>{value ?? "—"}</div>
        </div>
    )
}

const weatherCard = {
    wrapper: css`
        background: ${colors.eventLight};
        border-radius: 5px;
        padding: 0.6em;
        display: grid;
        grid-template: 1.5em 1.5em / 3em 1fr;
        grid-template-areas: "icon label" "icon value";
        gap: 0.2em;
        align-items: center;
        min-width: 10em;
        position: relative;
        overflow: hidden;
    `,
    icon: css`
        grid-area: icon;
        svg { width: 100%; height: 100%; padding: 4px; }
    `,
    label: css`
        grid-area: label;
        color: ${colors.soft};
        font-size: 0.8em;
    `,
    value: css`
        grid-area: value;
        font-size: 1.2em;
        color: ${colors.white};
    `,
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const style = {
    grid: css`
        display: grid;
        gap: 1.5em 3em;
        grid-template-columns: 18em auto;

        @media screen and (max-width: 600px) {
            grid-template-columns: 1fr;
        }
    `,
    fieldColumn: css`
        display: flex;
        flex-direction: column;
        gap: 0.5em;
    `,
    labelRow: css`
        display: flex;
        align-items: center;
        gap: 0.5em;
    `,
    datePickerCta: css`
        flex: 1;
        > button {
            align-items: center;
            border-width: 1px;
            background-color: ${colors.eventDark};
        }
    `,
    weatherCta: css`
        background-color: ${colors.eventDark};
        border-width: 1px;
        font-size: 0.875em;
        padding: 0.5em;
    `,
    weatherCards: (hasData) => css`
        display: flex;
        gap: 0.5em;
        flex-wrap: wrap;
        opacity: ${hasData ? 1 : 0.3};
        transition: opacity 0.3s;
        pointer-events: ${hasData ? 'auto' : 'none'};

        @media screen and (max-width: 600px) {
            flex-direction: column;
        }
    `,
    forecastLabel: css`
        font-size: 0.8em;
        color: ${colors.soft};
        min-height: 1.2em;
    `,
    fullWidthCell: css`
        grid-column: span 2;

        @media screen and (max-width: 600px) {
            grid-column: span 1;
        }
    `,
    cardRow: css`
        display: flex;
        gap: 0.5em;
        flex-wrap: wrap;
    `,
    sectionHeading: css`
        margin-bottom: 0.5em;
    `,
    bottomSection: css`
        grid-column: span 2;
        display: flex;
        flex-wrap: wrap;
        gap: 2em;

        @media screen and (max-width: 600px) {
            grid-column: span 1;
        }
    `,
    wornAreasWrapper: css`
        width: min-content;
    `,
    wornAreasHeading: css`
        margin-bottom: 20px;
    `,
    wornAreasOverflow: css`
        max-width: 100%;
        overflow-x: auto;
    `,
    noteWrapper: css`
        flex-grow: 1;
        min-width: 20em;
        width: 20em;
    `,
    wysiwygEditor: css`
        margin-top: 0.5em;
    `,
    compass: css`
        position: relative;
        height: 2em;
        width: 2em;
        bottom: -1em;
        left: calc(50% - 1em);
        background-color: rgba(44, 52, 58);
        box-shadow: none;
        margin-bottom: 40px;
    `,
}
